import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import Dashboard from './pages/Dashboard';
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';
import People from './pages/People';
import PersonDetail from './pages/PersonDetail';
import Applications from './pages/Applications';
import ApplicationDetail from './pages/ApplicationDetail';
import CandidateDetail from './pages/CandidateDetail';
import Reviews from './pages/Reviews';
import ReviewDetail from './pages/ReviewDetail';
import DeadLetterDetail from './pages/DeadLetterDetail';
import Settings from './pages/Settings';
import AdminPanel from './pages/AdminPanel';
import { SuperAdminRoute } from './components/SuperAdminRoute';
import Setup from './pages/Setup';
import Login from './pages/Login';
import SetupPassword from './pages/SetupPassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import MagicLinkVerify from './pages/MagicLinkVerify';
import PublicFeedback from './pages/PublicFeedback';
import TalentPool from './pages/TalentPool';
import Reports from './pages/Reports';
import DataRetentionDashboard from './pages/DataRetentionDashboard';
import AIModelConfig from './pages/AIModelConfig';
import Learning from './pages/Learning';
import GDPRErasure from './pages/GDPRErasure';
import LookupPage from './pages/Lookup';
import FunnelView from './pages/FunnelView';
import SourcingPage from './pages/SourcingPage';
import SourcingAgentCalibration from './pages/SourcingAgentCalibration';
import SourcingTuneEntry from './pages/SourcingTuneEntry';
import RbChat from './pages/RbChat';
import { ToastProvider } from './contexts/ToastContext';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/setup-password" element={<SetupPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/verify/:token" element={<MagicLinkVerify />} />
          <Route path="/feedback/:token" element={<PublicFeedback />} />

          {/* Setup route - requires auth but not configuration */}
          <Route path="/setup" element={<Setup />} />

          {/* Org-wide AI chat — shell-agnostic (no Layout/nav chrome) so it can be
              reused as a scoped PWA / Chrome-extension surface (US-234-style constraint). */}
          <Route
            path="/rb"
            element={
              <ProtectedRoute>
                <RbChat />
              </ProtectedRoute>
            }
          />

          {/* Protected routes - require authentication and configuration */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="jobs/:id" element={<JobDetail />} />
            <Route path="jobs/:id/funnel" element={<FunnelView />} />
            <Route path="jobs/:id/sourcing" element={<SourcingPage />} />
            <Route path="jobs/:id/sourcing/tune" element={<SourcingTuneEntry />} />
            <Route path="sourcing-agents/:agentId/calibration/:roundId" element={<SourcingAgentCalibration />} />

            {/* People (replaces Candidates + Applications as primary nav) */}
            <Route path="people" element={<People />} />
            <Route path="people/:id" element={<PersonDetail />} />

            {/* Lookup by SR application ID — Chrome extension reuse surface (US-234) */}
            <Route path="lookup" element={<LookupPage />} />

            {/* Talent Pool (US-088) */}
            <Route path="talent-pool" element={<TalentPool />} />

            {/* Reports (US-089) */}
            <Route path="reports" element={<Reports />} />

            {/* Data Retention Dashboard (US-067) */}
            <Route path="data-retention" element={<DataRetentionDashboard />} />

            {/* AI Configuration (US-075-079) — canonical URL is /settings/ai-config */}
            <Route path="settings/ai-config" element={<AIModelConfig />} />
            <Route path="ai-config" element={<Navigate to="/settings/ai-config" replace />} />

            {/* AI Learning System (Sprint 12: US-104-108) */}
            <Route path="learning" element={<Learning />} />

            {/* GDPR Erasure (US-069) */}
            <Route path="gdpr-erasure" element={<GDPRErasure />} />

            {/* Legacy routes kept for bookmarks/deep links */}
            <Route path="applications" element={<Applications />} />
            <Route path="applications/:id" element={<ApplicationDetail />} />
            <Route path="candidates" element={<Navigate to="/people" replace />} />
            <Route path="candidates/:id" element={<CandidateDetail />} />
            <Route path="reviews" element={<Reviews />} />
            <Route path="reviews/:id" element={<ReviewDetail />} />
            <Route path="dead-letter" element={<Navigate to="/settings" replace />} />
            <Route path="dead-letter/:id" element={<DeadLetterDetail />} />
            <Route path="settings" element={<Settings />} />
            <Route
              path="admin"
              element={
                <SuperAdminRoute>
                  <AdminPanel />
                </SuperAdminRoute>
              }
            />
          </Route>
          </Routes>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
